import type { ScaleConfig } from "@/types/legend.types";
import {
  BY_LAYER_LINEWEIGHT,
  ENTITY_LINEWEIGHTS,
  LAYER_DEFAULT_LINEWEIGHT,
  LAYER_LINEWEIGHTS,
} from "@/drawing/lineweight/utils";

type TranslateFn = (key: string) => string;

function formatMm(value: number): string {
  return `${(value / 100).toFixed(2)} mm`;
}

// Exporté pour être réutilisé par le viewer de légende du canvas (affichage lecture-seule).
// `t` vient de useTranslation() côté appelant — les labels "Par calque"/"Par défaut" doivent
// suivre la langue active, contrairement au formatage numérique ("X.XX mm") qui est invariant.
export function lineweightLabel(value: number, t: TranslateFn): string {
  if (value === BY_LAYER_LINEWEIGHT) return t("dashboard:by_layer.lineweight");
  if (value === LAYER_DEFAULT_LINEWEIGHT) return t("dashboard:by_default.lineweight");
  return formatMm(value);
}

// Pour un calque : jamais de valeur "Par calque" ici (un calque n'a pas de "calque parent"
// dont hériter), mais "Par défaut" (LAYER_DEFAULT_LINEWEIGHT) reste une option — c'est ce
// qu'AutoCAD assigne nativement à tout calque neuf. Fonction (pas une const figée au
// chargement du module) pour rester à jour avec la langue active, comme getNoteLineweightOptions.
export function getLayerLineweightOptions(t: TranslateFn) {
  return LAYER_LINEWEIGHTS.map((value) => ({
    value,
    label: lineweightLabel(value, t),
  }));
}

// Pour une NoteConfig (pas un calque) : ajoute l'option "Par calque" (ByLayer). Fonction
// (pas une const figée au chargement du module) pour rester à jour avec la langue active.
export function getNoteLineweightOptions(t: TranslateFn) {
  return ENTITY_LINEWEIGHTS.map((value) => ({
    value,
    label: lineweightLabel(value, t),
  }));
}

// Label affiché pour un linetype — seule la valeur "ByLayer" (comprise par le moteur,
// voir resolveLinetype) est traduite ; la valeur persistée ne change pas.
export function linetypeLabel(value: string, t: TranslateFn): string {
  if (value === "ByLayer") return t("dashboard:by_layer.linetype");
  return value;
}

// Générique pour préserver le shape exact de l'entrée : appelé avec un ScaleConfig
// complet (édition) il rend un ScaleConfig, appelé avec le gabarit sans id (création,
// voir ScalesConfigSection.addScale) il rend un Omit<ScaleConfig, "id">.
export const editScaleName = <T extends Omit<ScaleConfig, "id">>(
  scale: T,
  name: string
): T => {
  return {
    ...scale,
    name,
    props: {
      ...scale.props,
      textstyle: {
        ...scale.props.textstyle,
        name,
      },
    },
  };
};
